import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";

const STATUS_DOT = {
  pending: "bg-amber-400/20 text-amber-200 border-amber-400/30",
  in_progress: "bg-blue-400/20 text-blue-200 border-blue-400/30",
  review: "bg-violet-400/20 text-violet-200 border-violet-400/30",
  completed: "bg-emerald-400/20 text-emerald-200 border-emerald-400/30",
  rejected: "bg-rose-400/20 text-rose-200 border-rose-400/30",
};

const pad = (n) => String(n).padStart(2, "0");
const key = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export default function Calendar() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.filter((e) => e.status !== "cancelled"))).catch(() => {});
  }, []);

  const eventsByDay = useMemo(() => {
    const m = {};
    for (const e of events) {
      const d = new Date(e.start_date + "T00:00:00");
      const end = new Date(e.end_date + "T00:00:00");
      while (d <= end) { (m[key(d)] ||= []).push(e); d.setDate(d.getDate() + 1); }
    }
    return m;
  }, [events]);

  const byDay = useMemo(() => {
    const m = {};
    for (const o of orders) if (o.deadline) (m[o.deadline.slice(0, 10)] ||= []).push(o);
    return m;
  }, [orders]);

  const days = useMemo(() => {
    const first = new Date(cursor);
    const offset = (first.getDay() + 6) % 7; // Monday first
    const start = new Date(first); start.setDate(1 - offset);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const todayKey = key(new Date());
  const monthLabel = cursor.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { month: "long", year: "numeric" });
  const weekdays = lang === "fr" ? ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const shift = (n) => setCursor((c) => { const d = new Date(c); d.setMonth(d.getMonth() + n); return d; });
  const monthCount = orders.filter((o) => o.deadline && o.deadline.slice(0, 7) === `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`).length;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("calendar")}</h1>
          <p className="text-slate-400 mt-1 text-sm capitalize" data-testid="calendar-month-label">{monthLabel} · {monthCount} {t("deadline").toLowerCase()}(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="calendar-prev" onClick={() => shift(-1)} className="border-white/10 bg-white/5"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" data-testid="calendar-today" onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }} className="border-white/10 bg-white/5">{t("today")}</Button>
          <Button variant="outline" size="icon" data-testid="calendar-next" onClick={() => shift(1)} className="border-white/10 bg-white/5"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-white/5">
          {weekdays.map((w) => <div key={w} className="px-3 py-2 text-[11px] uppercase tracking-widest text-slate-400">{w}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const k = key(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const list = byDay[k] || [];
            const isToday = k === todayKey;
            const overdue = list.some((o) => k < todayKey && !["completed", "rejected"].includes(o.status));
            return (
              <div key={k} data-testid={`calendar-day-${k}`} className={`min-h-[110px] border-b border-r border-white/5 p-2 ${inMonth ? "" : "opacity-30"} ${overdue ? "bg-rose-500/5" : ""}`}>
                <div className={`text-xs mb-1.5 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-indigo-500 text-white font-bold" : "text-slate-400"}`}>{d.getDate()}</div>
                <div className="space-y-1">
                  {(eventsByDay[k] || []).map((e) => (
                    <button
                      key={e.id}
                      data-testid={`calendar-event-${e.id}`}
                      onClick={() => nav(`/events?open=${e.id}`)}
                      title={e.name}
                      className="w-full text-left text-[11px] px-1.5 py-0.5 rounded bg-fuchsia-500/20 border border-fuchsia-400/40 text-fuchsia-100 truncate hover:brightness-125 font-medium"
                    >
                      ★ {e.name}
                    </button>
                  ))}
                  {list.slice(0, 3).map((o) => (
                    <button
                      key={o.id}
                      data-testid={`calendar-order-${o.id}`}
                      onClick={() => nav(`/orders?open=${o.id}`)}
                      title={`${o.title} · ${t(o.status)}`}
                      className={`w-full text-left text-[11px] px-1.5 py-0.5 rounded border truncate hover:brightness-125 ${STATUS_DOT[o.status]}`}
                    >
                      {o.title}
                    </button>
                  ))}
                  {list.length > 3 && <div className="text-[10px] text-slate-400 pl-1">+{list.length - 3}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
